/**
 * Every failure the UI can meet, as a closed set.
 *
 * 🔴 The reason this is an enum and not a string message: the view has to be able to say WHICH
 * failure happened. "Could not load tickets" and "no tickets match this filter" must be
 * distinguishable by a reader, and so must "PlanHub is down" and "PlanHub is up but cannot reach
 * GitLab". A single `error: string` collapses all four into one grey box.
 */
export type ApiErrorKind =
  | 'offline' // the request never reached the API (DNS, TLS, no network, CORS)
  | 'unauthenticated' // 401 — session absent or expired
  | 'forbidden' // 403 — logged in, not allowed
  | 'not_found' // 404
  | 'conflict' // 409 — e.g. illegal transition, still-referenced vocabulary row
  | 'upstream_unavailable' // 503 — the API is up but a dependency (GitLab) is not
  | 'server' // 5xx that is not 503
  | 'bad_response' // 2xx whose body was not the shape we require
  | 'http'; // any other non-2xx

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  /** The API's own explanation, when it gave one. Shown verbatim — it is the useful half of a 503. */
  readonly reason: string | null;
  readonly url: string;

  constructor(args: {
    kind: ApiErrorKind;
    status?: number | null;
    reason?: string | null;
    url: string;
    message?: string;
  }) {
    super(args.message ?? defaultMessage(args.kind));
    this.name = 'ApiError';
    this.kind = args.kind;
    this.status = args.status ?? null;
    this.reason = args.reason ?? null;
    this.url = args.url;
  }
}

export function classifyStatus(status: number): ApiErrorKind {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 503) return 'upstream_unavailable';
  if (status >= 500) return 'server';
  return 'http';
}

/**
 * Headline text for a failure. Deliberately says what is broken rather than "something went wrong":
 * the user's next action differs completely between "sign in again" and "GitLab is down".
 */
export function defaultMessage(kind: ApiErrorKind): string {
  switch (kind) {
    case 'offline':
      return 'Cannot reach the PlanHub API';
    case 'unauthenticated':
      return 'Your session has expired';
    case 'forbidden':
      return 'You do not have access to this';
    case 'not_found':
      return 'Not found';
    case 'conflict':
      return 'That change was refused';
    case 'upstream_unavailable':
      return 'PlanHub cannot reach GitLab right now';
    case 'server':
      return 'The PlanHub API returned an error';
    case 'bad_response':
      return 'The PlanHub API returned an unexpected response';
    case 'http':
      return 'The request was refused';
  }
}

/** What the reader should do about it. Empty string when there is nothing useful to say. */
export function remedyFor(kind: ApiErrorKind): string {
  switch (kind) {
    case 'offline':
      return 'Check your connection, then retry.';
    case 'unauthenticated':
      return 'Sign in again to continue.';
    case 'upstream_unavailable':
      return 'PlanHub planning data is unaffected. Anything that reads GitLab is unavailable until it returns.';
    case 'server':
    case 'bad_response':
      return 'This is a problem on the server, not with what you did.';
    default:
      return '';
  }
}

export function toApiError(err: unknown, url: string): ApiError {
  if (err instanceof ApiError) return err;
  // fetch() rejects only on a genuine network-level failure; every HTTP status resolves.
  return new ApiError({
    kind: 'offline',
    url,
    reason: err instanceof Error ? err.message : null,
  });
}
